import { writable } from "svelte/store";
import type { AuthStates } from "../types/auth-states";
import type { AuthUser } from "../types/auth-user";

export type AuthState = AuthStates;

export const AuthStateValues = {
  unknown: "unknown",
  authed: "authed",
  unauth: "unauth"
} as const satisfies Record<string, AuthStates>;

export const authState = writable<AuthState>(AuthStateValues.unknown);
export const authUser = writable<AuthUser>(null);

export const login = (user: AuthUser) => {
  authUser.set(user);
  authState.set(AuthStateValues.authed);
};

export const logout = () => {
  authUser.set(null);
  authState.set(AuthStateValues.unauth);
};

export type LoginResult =
  | { ok: true }
  | { ok: false; message: string };

export const loginWithPassword = async (
  email: string,
  password: string
): Promise<LoginResult> => {
  try {
    const response = await fetch("/login/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      return { ok: false, message: error?.message ?? "Login failed." };
    }

    const user = (await response.json().catch(() => ({}))) as AuthUser;
    login(user);
    return { ok: true };
  } catch (error) {
    console.error("Login failed", error);
    return { ok: false, message: "Login failed." };
  }
};

export const logoutUser = async (): Promise<void> => {
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include"
    });
  } catch (error) {
    console.error("Logout failed", error);
  } finally {
    logout();
  }
};
